import * as React from 'react';

import {RouteComponentProps} from 'react-router';
import LessonApi from '../modules/lessons/api/LessonApi';
import Lesson from '../modules/lessons/domain/aggregates/lesson/Lesson';
import {Link} from 'react-router-dom';
import Page from './Page';
import LessonResultApi from '../modules/lessons/api/LessonResultApi';
import EntityId from '../../foundation/domain/EntityId';
import ExerciseApi from '../modules/lessons/api/ExerciseApi';
import { Button, Table, Breadcrumb, Popup, Icon, Label, Dropdown, Header } from 'semantic-ui-react';
import Exercise from '../modules/lessons/domain/aggregates/exercise/Exercise';
import Course from '../modules/lessons/domain/aggregates/course/Course';
import CourseApi from '../modules/lessons/api/CourseApi';
import LessonResult from '../modules/lessons/domain/aggregates/lessonResult/LessonResult';
import Registry from '../global/Registry';
import User from '../modules/users/domain/aggregates/user/User';
import * as Voca from 'voca';
import BBMLParser from './components/exercises/BBMLParser';
import ReviewReminder from './components/lesson/ReviewReminder';
import SectionSpawner from '../global/SectionSpawner';

interface Props {
	id?: string;
}

interface State {
	lessons: Lesson[];
	exercisesToReview: Exercise[];
	lessonResults: LessonResult[];
	course?: Course;
}

export default class CoursePage extends Page<RouteComponentProps<Props>, State> {

	public constructor (props: RouteComponentProps<Props>) {
		super(props);

		this.state = { lessons: [], exercisesToReview: [], lessonResults: []};
	}

	async componentWillMount () {
		const [lessons, exercisesToReview, lessonResults] = await Promise.all([
			LessonApi.getInstance().getAll(),
			ExerciseApi.getInstance().getAllToRepeatByUserId(Registry.getInstance().me.getId()),
			LessonResultApi.getInstance().getAllByUserId(Registry.getInstance().me.getId()),
			CourseApi.getInstance().getAll()
		]);

		const course = Registry.getInstance().courseRepository.getOneOrUndefinedByIdValue(this.props.match.params.id);

		this.setState({lessons, exercisesToReview, lessonResults, course});
	}

	async componentWillReceiveProps (props: RouteComponentProps<Props>) {
		const course = Registry.getInstance().courseRepository.getOneOrUndefinedByIdValue(props.match.params.id);

		this.setState({course});
	}

	protected isLoading (): any {
		return this.state.course === undefined;
	}

	protected renderTitle (): string {
		return 'Brain Brooder - Digital Math & Science Education';
	}

	protected renderDescription (): string {
		return 'Learn Math and Science deeply, by taking small but dense lessons that are followed by intelligently repeated exercises, such that you never forget.';
	}

	protected renderContent () {
		const course = this.state.course as Course;

		const sections = SectionSpawner.course(course);

		const me = Registry.getInstance().me;

		const courseLessons = this.state.lessons.filter(lesson => lesson.getCourseId().equals(course.getId()) && lesson.isActive());

		const dropdown = (
			<Dropdown icon='cogs' pointing='top right' floating compact button className='icon right floated inverted' style={{marginTop: '29px'}}>
				<Dropdown.Menu>
					<Dropdown.Item icon='pencil' text='Rename' onClick={() => Page.navigate(course.getSlug() + '/edit')} />
				</Dropdown.Menu>
			</Dropdown>
		);

		return (
			<div className='lesson'>
				<Breadcrumb icon='right angle' sections={sections} />

				{me.isErik() || me.getId().equals(course.getUserId()) ? dropdown : null}

				<Header as='h1' dividing inverted content={BBMLParser.execute(course.getName())} subheader={''} />

				<ReviewReminder courseId={course.getId()} />

				{courseLessons.length === 0 ? (
					<div style={{textAlign: 'center'}}>
						<div style={{margin: '48px 0'}}>
							<h2>This course doesn't contain any lessons yet.</h2>
						</div>
						<p><Button inverted size={'big'} color={'green'} onClick={() => Page.navigate('/lesson/new/' + course.getId().getValue())}><Icon name={'plus'} />  Create the first lesson</Button></p>
					</div>
				) : (
					<div>
						<LessonsTable user={me} course={course} courseLessons={courseLessons} lessons={this.state.lessons} lessonResults={this.state.lessonResults} />

						{me.isErik() || me.getId().equals(course.getUserId()) ? <Link to={'/lesson/new/' + course.getId().getValue()}><Button inverted color={'grey'} fluid style={{marginTop: '36px'}}><Icon name={'plus'} />  Create a new lesson</Button></Link> : null}
					</div>
				)}

				{course.getId().getValue() === 9 ? <p style={{fontSize: '0.8em', textAlign: 'center'}}>New lessons are continuously added.<br />Estimated course completion: March 2018</p> : null}
			</div>
		);
	}
}

class LessonRowData {

	constructor (private lesson: Lesson, private completed: boolean, private prerequisiteLessonIds: EntityId<Lesson>[], private completedPrerequisiteLessonIds: EntityId<Lesson>[]) {}

	public getLesson () {
		return this.lesson;
	}

	public getPrerequisiteLessonIds () {
		return this.prerequisiteLessonIds;
	}

	public hasUnmetPrerequisites () {
		return this.getNumberOfUnmetPrerequisites() > 0;
	}

	public getNumberOfUnmetPrerequisites () {
		return this.getNumberOfPrerequisiteLessons() - this.getNumberOfCompletedPrerequisiteLessons();
	}

	public getNumberOfPrerequisiteLessons () {
		return this.prerequisiteLessonIds.length;
	}

	public getNumberOfCompletedPrerequisiteLessons () {
		return this.completedPrerequisiteLessonIds.length;
	}

	public getSortingKey () {
		const splitTitle = this.lesson.getTitle().toLocaleLowerCase().split('.');
		const title = splitTitle.map(titlePart => {
			const matches = titlePart.match(/^(\d+)(.*)$/);

			return matches !== null ? Voca.padLeft(matches[1], 4, '0') + matches[2] : titlePart;
		}).join('.');

		return (this.completed ? '0' : '1') + (Voca.padLeft(String(this.prerequisiteLessonIds.length), 10, '0')) + (9999999999 - this.getNumberOfCompletedPrerequisiteLessons()) + title;
	}

	public isCompleted () {
		return this.completed;
	}
}

const createLessonRowsData = (courseLessons: Lesson[], lessons: Lesson[], lessonResults: LessonResult[]) => {

	return courseLessons.map(lesson => {
		const prerequisiteLessonIds: EntityId<Lesson>[] = [];
		let source = lesson.getPrerequisiteLessonIds().slice();

		while (source.length > 0) {
			const prerequisiteLessonId = source.pop() as EntityId<Lesson>;

			if (!prerequisiteLessonIds.some(lessonId => lessonId.equals(prerequisiteLessonId))) {
				prerequisiteLessonIds.push(prerequisiteLessonId);

				const prerequisiteLesson = lessons.find(lesson => lesson.getId().equals(prerequisiteLessonId)) as Lesson;

				source = source.concat(prerequisiteLesson.getPrerequisiteLessonIds());
			}
		}

		const completed = lessonResults.some(result => result.getLessonId().equals(lesson.getId()) && result.isCompleted());
		const completedPrerequisiteLessonIds = prerequisiteLessonIds.filter(lessonId => lessonResults.some(result => result.getLessonId().equals(lessonId) && result.isCompleted()));

		return new LessonRowData(lesson, completed, prerequisiteLessonIds, completedPrerequisiteLessonIds);
	});
};

const LessonsTable: React.SFC<{user: User, course: Course, courseLessons: Lesson[], lessons: Lesson[], lessonResults: LessonResult[]}> = ({user, course, courseLessons, lessons, lessonResults}) => {

	const lessonRowsData = createLessonRowsData(courseLessons, lessons, lessonResults);

	return (
		<Table inverted selectable unstackable>
			<Table.Header>
				<Table.Row>
					<Table.HeaderCell colSpan={2}>Lessons</Table.HeaderCell>
				</Table.Row>
			</Table.Header>
			<Table.Body>
				{lessonRowsData.sort((a, b) => a.getSortingKey().localeCompare(b.getSortingKey())).map((lessonRowData, key) => <LessonRow key={key} lessonRowData={lessonRowData} lessonRowsData={lessonRowsData} />)}
			</Table.Body>
		</Table>
	);
};

const LessonRow: React.SFC<{lessonRowData: LessonRowData, lessonRowsData: LessonRowData[]}> = ({lessonRowData, lessonRowsData}) => {

	const lesson = lessonRowData.getLesson();

	return (
		<Table.Row onClick={() => Page.navigate(lesson.getSlug())} className='completed' style={{color: lessonRowData.hasUnmetPrerequisites() ? '#666666' : 'inherit'}}>
			<Table.Cell>
				<a style={{color: 'inherit'}} href={lesson.getSlug()} onClick={(event) => event.preventDefault()}>{BBMLParser.execute(lesson.getTitle())}</a>
			</Table.Cell>
			<Table.Cell style={{textAlign: 'right'}}>
				<Popup hoverable position='bottom right' trigger={<span>{lessonRowData.isCompleted() ? <Icon name='checkmark' color='blue' /> : !lessonRowData.hasUnmetPrerequisites() ? <Icon name='angle right' /> : <Label color='grey' circular onClick={(event) => event.stopPropagation()}>{lessonRowData.getNumberOfUnmetPrerequisites()}</Label>}</span>}>
					<Popup.Header>{lessonRowData.isCompleted() ? 'Completed' : lessonRowData.hasUnmetPrerequisites() ? 'Unmet prerequisites' : 'No unmet prerequisites'}</Popup.Header>
					<Popup.Content>
						{lessonRowsData.filter(innerLessonRowData => lessonRowData.getPrerequisiteLessonIds().some(lessonId => !innerLessonRowData.isCompleted() && lessonId.equals(innerLessonRowData.getLesson().getId()))).map((lessonRowData, key) => <LessonLink key={key} lessonRowData={lessonRowData} />)}
					</Popup.Content>
				</Popup>
			</Table.Cell>
		</Table.Row>
	);
};

const LessonLink: React.SFC<{lessonRowData: LessonRowData}> = ({lessonRowData}) => {

	const lesson = lessonRowData.getLesson();

	return <div><Link to={lesson.getSlug()} style={{color: lessonRowData.hasUnmetPrerequisites() ? '#666666' : 'default'}}>{lesson.getTitle()}</Link></div>;
};


// WEBPACK FOOTER //
// ./code/project/pages/CoursePage.tsx